'use memo';

import { useContext, useEffect, useRef, useState } from 'react';
import {
  AgxButton,
  AgxNotificationBox,
  AgxSpacer,
  AgxTypography,
  useWindowSize,
  Breakpoints,
  AgxSegmentPicker,
  useToast,
} from '@urbanx/agx.ui-components2';
import {
  FormConfigType,
  MarketingPackageStage,
  AustralianState,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import { useAppSelector } from 'hooks/useAppSelector';
import { AuthContext } from '@urbanx/agx.auth.client';
import { beginCampaignForm } from 'Api/Campaigns/campaignsApi';
import { useFormLoader } from 'hooks/useFormLoader';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import FormDetailsHeader from '../Common/FormDetailsHeader';
import ListingDetails, { ListingDetailsHandle } from './Common/ListingDetails';
import ContractDetails from './Common/ContractDetails';
import MarketingPackageSigningPanel from './MarketingPackage/MarketingPackageSigningPanel';
import ResumeMarketingPackage from './MarketingPackage/ResumeMarketingPackage';
import MarketingPackageCompletePanel from './MarketingPackage/MarketingPackageCompletePanel';
import { Tabs, ListingTabs } from 'types/Tabs';
import './ListingInfoPanel.scss';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  FormType,
} from '@urbanx/agx.campaigns.client';
import LaunchDetails from './Common/LaunchDetails';
import {
  MarketingPackageProjection,
  useGetMarketingPackages,
  useInvalidateUseGetMarketingPackages,
} from '@urbanx/agx.marketing.client';
import ContractFormButtons from './Common/ContractFormButtons';

interface ListingInfoPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ListingInfoPanel = ({
  campaign,
  currentTab,
  mobile = false,
}: ListingInfoPanelProps) => {
  const listingDetailsRef = useRef<ListingDetailsHandle>(null);
  const screenSize = useWindowSize();
  const [selectedTab, setSelectedTab] = useState(ListingTabs.Contracts);
  const [disableButtons, setDisableButtons] = useState(false);
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm } = useFormLoader();
  const { pushToast } = useToast();
  const [selectedMarketingPackage, setSelectedMarketingPackage] = useState<
    MarketingPackageProjection | undefined
  >();
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const { addMarketingToLiveListing, enableLaunchPad } = useFeatureToggles(
    campaign?.state
  );
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;

  const isArchived = !!campaign.completionState?.completionType;

  const { address, campaignId, blankContractIsLive, lastUpdated } = campaign;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const { data: marketingPackages } = useGetMarketingPackages(
    campaign.campaignId ?? '',
    addMarketingToLiveListing
  );

  const invalidateMarketingPackages = useInvalidateUseGetMarketingPackages();

  useEffect(() => {
    invalidateMarketingPackages(campaign.campaignId ?? '');
  }, [lastUpdated]);

  useEffect(() => {
    if (!selectedMarketingPackage) return;

    setSelectedMarketingPackage(
      marketingPackages?.find(mp => mp.id === selectedMarketingPackage.id)
    );
  }, [marketingPackages]);

  const segments = [
    {
      title: 'Contracts',
      value: ListingTabs.Contracts,
    },
    {
      title: 'Listing Details',
      value: ListingTabs.ListingDetails,
    },
    ...(enableLaunchPad
      ? [
          {
            title: 'Launch',
            value: ListingTabs.Launch,
          },
        ]
      : []),
  ];

  const lastUpdatedOn = `Last updated ${dayjs(lastUpdated).format('DD/MM/YY')}`;

  const findAvailableForm = (formType: FormType) =>
    availableForms.find(
      form =>
        form.type === formType &&
        (form.state as string | undefined) === campaign.state
    );

  const beginForm = async (formType: FormType, errorMessage: string) => {
    if (!getAuthToken) return;

    const form = findAvailableForm(formType);

    if (!form) return;

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newFormId = await beginCampaignForm(
        authToken,
        campaignId ?? '',
        form.type as FormType,
        form.state as AustralianState
      );

      if (!newFormId) {
        pushToast({
          type: 'error',
          message: errorMessage,
        });
        setDisableButtons(false);
        return;
      }

      await loadForm(campaignId ?? '', newFormId, FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
    }

    setDisableButtons(false);
  };

  const beginContract = () =>
    beginForm(FormType.ContractRequest, 'Failed to start Sales Advice');

  const beginContractExchange = () =>
    beginForm(FormType.ContractExchange, 'Failed to start Contract exchange');

  const beginSubmission = () =>
    beginForm(
      FormType.ContractSubmission,
      'Failed to start Contract submission'
    );

  if (selectedMarketingPackage) {
    switch (selectedMarketingPackage.stage?.value) {
      case MarketingPackageStage.Draft:
      case MarketingPackageStage.SubmissionIncomplete:
        return (
          <ResumeMarketingPackage
            onBack={() => setSelectedMarketingPackage(undefined)}
            campaign={campaign}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
          />
        );
      case MarketingPackageStage.VendorSigning:
        return (
          <MarketingPackageSigningPanel
            onBack={() => setSelectedMarketingPackage(undefined)}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
            campaign={campaign}
          />
        );
      case MarketingPackageStage.Complete:
        return (
          <MarketingPackageCompletePanel
            onBack={() => setSelectedMarketingPackage(undefined)}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
          />
        );
      default:
        break;
    }
  }

  return (
    <>
      <AgxSpacer
        className="listingInfoPanel"
        contentAlignment="center"
        gap="spacing-xl"
        padding={{ bottom: 'sm' }}
      >
        <FormDetailsHeader
          id="listing-info-panel-header"
          formName="Listing"
          detail={lastUpdatedOn}
          currentTab={currentTab}
          onBack={undefined}
        />
        <AgxSpacer
          fill
          contentAlignment="center"
          className="contractPanelTitle"
        >
          <AgxTypography
            id="agx-listing-info-panel-address"
            type="heading"
            size="2x-large"
            style={{
              textAlign: 'center',
            }}
          >
            {addressLineOne},
            <br />
            {addressLineTwo}
          </AgxTypography>
        </AgxSpacer>
        <AgxSpacer
          direction="vertical"
          fill
          contentAlignment="center"
          className="listingInfoPanelContent"
          gap="md"
          padding={
            screenSize === Breakpoints.Mobile ||
            screenSize === Breakpoints.Tablet_Portrait
              ? undefined
              : { left: 'spacing-7xl', right: 'spacing-7xl' }
          }
        >
          <AgxSpacer direction="horizontal" fill contentJustification="center">
            <AgxSpacer
              direction="horizontal"
              className="listingSegmentedPickerContainer"
            >
              <AgxSegmentPicker
                id="agx-listing-segments"
                segments={segments}
                defaultValue={selectedTab}
                onChange={value => setSelectedTab(value as ListingTabs)}
                fill
              />
            </AgxSpacer>
          </AgxSpacer>

          {selectedTab === ListingTabs.Contracts && (
            <AgxSpacer
              direction="horizontal"
              fill
              contentJustification="center"
            >
              <AgxSpacer className="listingSegmentedPickerContainer">
                <ContractDetails
                  campaign={campaign}
                  contractDetails={campaign.contractDetails ?? []}
                  completionState={campaign.completionState ?? {}}
                  propertyImageUrls={{
                    thumbnail: campaign.propertyImageUrls?.thumbnail,
                  }}
                  mobile={mobile}
                />
              </AgxSpacer>
            </AgxSpacer>
          )}

          {selectedTab === ListingTabs.ListingDetails && (
            <ListingDetails
              ref={listingDetailsRef}
              campaign={campaign}
              mobile={mobile}
              marketingPackages={marketingPackages ?? []}
              setSelectedMarketingPackage={setSelectedMarketingPackage}
            />
          )}
          {selectedTab === ListingTabs.Launch && (
            <AgxSpacer
              direction="horizontal"
              fill
              contentJustification="center"
            >
              <AgxSpacer className="listingSegmentedPickerContainer">
                <LaunchDetails campaign={campaign} />
              </AgxSpacer>
            </AgxSpacer>
          )}
        </AgxSpacer>

        {(!isNsw &&
          !campaign.blankContractIsLive &&
          (campaign.contractDetails?.length ?? 0) > 0) ||
        (isNsw && !campaign.areVendorsValid) ? (
          <div className="offlineContractAlert">
            <AgxNotificationBox
              type="warning"
              heading="New Contract generation is disabled."
              body={
                screenSize === Breakpoints.Mobile
                  ? 'Contact Support'
                  : 'Contact your administrator'
              }
            />
          </div>
        ) : null}

        {selectedTab === ListingTabs.Contracts && (
          <ContractFormButtons
            beginSubmission={beginSubmission}
            beginContract={beginContract}
            beginContractExchange={beginContractExchange}
            campaignState={campaign.state ?? CampaignDetailModelState.QLD}
            areVendorsValid={campaign.areVendorsValid ?? false}
            disableButtons={disableButtons}
            isArchived={isArchived}
            blankContractIsLive={blankContractIsLive ?? false}
          />
        )}
        {selectedTab === ListingTabs.ListingDetails && mobile && (
          <div className="campaignModalButtons">
            <AgxButton
              id="agx-infoPanel-addMarketingButton"
              variant="primary"
              text={`Add Marketing`}
              size="large"
              stretch={mobile}
              onClick={() => listingDetailsRef.current?.addMarketing()}
            />
          </div>
        )}
        {selectedTab === ListingTabs.Launch && (
          <div className="campaignModalButtons">
            <AgxButton
              id="agx-infoPanel-reviewAndLaunchButton"
              variant="hollow"
              text="Review & Launch"
              size="large"
              stretch={mobile}
              onClick={() => {}}
            />
          </div>
        )}
      </AgxSpacer>
    </>
  );
};

export default ListingInfoPanel;
